import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Typography, Button, TextField, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, FormControl, InputLabel, Select, MenuItem, Alert, RadioGroup, Radio, FormControlLabel, useMediaQuery } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import {
    getPricingHistories, getPricingHistoryItems,
    createPricingHistory, updatePricingHistory, deletePricingHistory,
    createPricingHistoryItem, updatePricingHistoryItem, deletePricingHistoryItem,
} from '../../api/resourceApi';

const PP = "'Poppins', sans-serif";

const shortRM = (val) => {
    if (val == null || val === 0) return '-';
    return `RM${Math.round(val).toLocaleString('en-MY')}`;
};

const statusOf = (history) => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const from = new Date(history.validFrom); from.setHours(0, 0, 0, 0);
    const until = history.validUntil ? new Date(history.validUntil) : null;
    if (until) until.setHours(0, 0, 0, 0);
    if (today < from) return 'future';
    if (until && today > until) return 'past';
    return 'active';
};

const statusDot = (status) => status === 'active' ? '🟢' : status === 'future' ? '🔵' : '⚪';

const formatRange = (h) => {
    const fromStr = new Date(h.validFrom).toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: 'numeric' });
    const untilStr = h.validUntil ? new Date(h.validUntil).toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: 'numeric' }) : 'ongoing';
    return `${fromStr} → ${untilStr}`;
};

export default function PriceHistoryTab({ canEdit = false } = {}) {
    const [histories, setHistories] = useState([]);
    const [selectedHistoryId, setSelectedHistoryId] = useState('');
    const [items, setItems] = useState([]);
    const [versionDialogOpen, setVersionDialogOpen] = useState(false);
    const [versionDialogMode, setVersionDialogMode] = useState('create');
    const [versionForm, setVersionForm] = useState({ name: '', validFrom: '', validUntil: '', note: '', seedChoice: 'live', copyFromHistoryId: '' });
    const [itemDialogOpen, setItemDialogOpen] = useState(false);
    const [itemEdit, setItemEdit] = useState(null);
    const isMobile = useMediaQuery('(max-width:768px)');

    const fetchHistories = useCallback(async () => {
        try {
            const res = await getPricingHistories();
            const data = res.data || [];
            setHistories(data);
            if (data.length > 0 && !selectedHistoryId) {
                const active = data.find(h => statusOf(h) === 'active');
                setSelectedHistoryId((active || data[0]).historyId);
            }
        } catch (e) { console.error(e); }
    }, [selectedHistoryId]);

    const fetchItems = useCallback(async () => {
        if (!selectedHistoryId) { setItems([]); return; }
        try {
            const res = await getPricingHistoryItems(selectedHistoryId);
            setItems(res.data || []);
        } catch (e) { console.error(e); }
    }, [selectedHistoryId]);

    useEffect(() => { fetchHistories(); }, [fetchHistories]);
    useEffect(() => { fetchItems(); }, [fetchItems]);

    const selectedHistory = useMemo(() => histories.find(h => h.historyId === selectedHistoryId) || null, [histories, selectedHistoryId]);
    const selectedStatus = selectedHistory ? statusOf(selectedHistory) : null;

    const openCreateVersion = () => {
        setVersionDialogMode('create');
        setVersionForm({ name: '', validFrom: '', validUntil: '', note: '', seedChoice: 'live', copyFromHistoryId: selectedHistoryId || '' });
        setVersionDialogOpen(true);
    };
    const openEditVersion = () => {
        if (!selectedHistory) return;
        setVersionDialogMode('edit');
        setVersionForm({
            name: selectedHistory.name,
            validFrom: selectedHistory.validFrom ? selectedHistory.validFrom.slice(0, 10) : '',
            validUntil: selectedHistory.validUntil ? selectedHistory.validUntil.slice(0, 10) : '',
            note: selectedHistory.note || '',
            seedChoice: 'none',
            copyFromHistoryId: '',
        });
        setVersionDialogOpen(true);
    };
    const saveVersion = async () => {
        if (!versionForm.name || !versionForm.validFrom) {
            alert('Name and ValidFrom are required');
            return;
        }
        try {
            if (versionDialogMode === 'create') {
                const body = {
                    name: versionForm.name,
                    validFrom: versionForm.validFrom,
                    validUntil: versionForm.validUntil || null,
                    note: versionForm.note || null,
                    copyFromLive: versionForm.seedChoice === 'live',
                    copyFromHistoryId: versionForm.seedChoice === 'existing' && versionForm.copyFromHistoryId ? Number(versionForm.copyFromHistoryId) : null,
                };
                const res = await createPricingHistory(body);
                await fetchHistories();
                setSelectedHistoryId(res.data.historyId);
            } else {
                const body = {
                    historyId: selectedHistoryId,
                    name: versionForm.name,
                    validFrom: versionForm.validFrom,
                    validUntil: versionForm.validUntil || null,
                    note: versionForm.note || null,
                };
                await updatePricingHistory(body);
                await fetchHistories();
            }
            setVersionDialogOpen(false);
        } catch (e) { alert('Save failed: ' + (e.message || e)); }
    };
    const handleDeleteVersion = async () => {
        if (!selectedHistory) return;
        if (!window.confirm(`Delete snapshot "${selectedHistory.name}" and all its rows?`)) return;
        try {
            await deletePricingHistory(selectedHistoryId);
            setSelectedHistoryId('');
            await fetchHistories();
        } catch (e) { alert('Delete failed'); }
    };

    const openAddItem = (listName) => {
        setItemEdit({ historyId: selectedHistoryId, listName, note: '', panelQty: 0, energy: 0, inverterSize: '', cashPrice: 0, cC36Price: 0, cC60Price: 0, cash3p8k: null, cC36_3p8k: null, cC60_3p8k: null, inverterSize3p: '', sortOrder: 0 });
        setItemDialogOpen(true);
    };
    const openEditItem = (item) => { setItemEdit({ ...item }); setItemDialogOpen(true); };
    const saveItem = async () => {
        if (!itemEdit) return;
        try {
            if (itemEdit.historyItemId) await updatePricingHistoryItem(itemEdit);
            else await createPricingHistoryItem(itemEdit);
            setItemDialogOpen(false); setItemEdit(null);
            await fetchItems();
        } catch (e) { alert('Save failed'); }
    };
    const handleDeleteItem = async (id) => {
        if (!window.confirm('Delete this row?')) return;
        await deletePricingHistoryItem(id);
        await fetchItems();
    };

    const grouped = {};
    items.forEach(i => { (grouped[i.listName] ||= []).push(i); });

    const isBatteryList = (name) => name === 'Battery Pricing';

    const f = (field) => (e) => setItemEdit(p => ({ ...p, [field]: e.target.value }));
    const fn = (field) => (e) => setItemEdit(p => ({ ...p, [field]: parseFloat(e.target.value) || 0 }));
    const isBatteryEdit = itemEdit && isBatteryList(itemEdit.listName);

    return (
        <Box>
            <Alert severity="info" sx={{ mb: 2, fontSize: '0.78rem', fontFamily: PP }}>
                Archive / reference only. Sales forms, RSC, CSC, and Form Generator always use the <b>Live Pricing</b> tab.
            </Alert>

            <Box sx={{ display: 'flex', gap: 1.5, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <FormControl size="small" sx={{ minWidth: 320 }}>
                    <InputLabel>Snapshot</InputLabel>
                    <Select value={selectedHistoryId} onChange={e => setSelectedHistoryId(e.target.value)} label="Snapshot">
                        {histories.length === 0 && <MenuItem value="" disabled>No snapshots yet</MenuItem>}
                        {histories.map(h => (
                            <MenuItem key={h.historyId} value={h.historyId}>
                                {statusDot(statusOf(h))} {h.name} ({formatRange(h)})
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                {canEdit && (
                    <Button size="small" startIcon={<AddIcon />} variant="contained" onClick={openCreateVersion}
                        sx={{ fontFamily: PP, fontWeight: 600, textTransform: 'none', borderRadius: '8px', background: 'linear-gradient(135deg, #1A8CFF, #0F62CC)', boxShadow: 'none' }}>
                        + New Snapshot
                    </Button>
                )}
                {canEdit && selectedHistory && (
                    <>
                        <Button size="small" variant="outlined" onClick={openEditVersion}
                            sx={{ fontFamily: PP, fontWeight: 600, textTransform: 'none', borderRadius: '8px' }}>
                            Edit Dates
                        </Button>
                        <Button size="small" variant="outlined" color="error" onClick={handleDeleteVersion}
                            sx={{ fontFamily: PP, fontWeight: 600, textTransform: 'none', borderRadius: '8px' }}>
                            Delete
                        </Button>
                    </>
                )}
            </Box>

            {selectedHistory && (
                <Typography sx={{ fontSize: '0.75rem', fontFamily: PP, color: '#64748B', mb: 2 }}>
                    {statusDot(selectedStatus)} {selectedStatus === 'active' ? 'Active period' : selectedStatus === 'future' ? 'Future (not yet started)' : 'Past'}
                    {selectedHistory.note && ` — ${selectedHistory.note}`}
                </Typography>
            )}

            {canEdit && selectedHistory && (
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    <Button size="small" startIcon={<AddIcon />} variant="outlined" onClick={() => openAddItem('Residential Pricing')}
                        sx={{ fontFamily: PP, fontWeight: 600, textTransform: 'none', borderRadius: '8px', fontSize: '0.75rem' }}>
                        + ATAP Pricing
                    </Button>
                    <Button size="small" startIcon={<AddIcon />} variant="outlined" onClick={() => openAddItem('Battery Pricing')}
                        sx={{ fontFamily: PP, fontWeight: 600, textTransform: 'none', borderRadius: '8px', fontSize: '0.75rem' }}>
                        + Battery Pricing
                    </Button>
                </Box>
            )}

            {Object.entries(grouped).map(([listName, rows]) => (
                <Box key={listName} sx={{ mb: 3 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', fontFamily: PP, mb: 1 }}>{listName}</Typography>
                    <Box sx={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: PP }}>
                            <thead>
                                <tr style={{ borderBottom: '2px solid #E2E8F0' }}>
                                    {isBatteryList(listName)
                                        ? ['Battery Type', 'kWh', 'Cash', '36M CC', '60M CC', ''].map(h => (
                                            <th key={h} style={{ padding: '8px 10px', fontSize: '0.72rem', fontWeight: 600, color: '#64748B', textAlign: h === 'Battery Type' ? 'left' : 'right', textTransform: 'uppercase' }}>{h}</th>
                                        ))
                                        : ['Panels', 'kWp', 'Inverter', 'Cash', '36M CC', '60M CC', '3P Cash', '3P 36M', '3P 60M', ''].map(h => (
                                            <th key={h} style={{ padding: '8px 10px', fontSize: '0.72rem', fontWeight: 600, color: '#64748B', textAlign: h === 'Panels' ? 'left' : 'right', textTransform: 'uppercase' }}>{h}</th>
                                        ))}
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map(item => isBatteryList(listName) ? (
                                    <tr key={item.historyItemId} style={{ borderBottom: '1px solid #F1F5F9' }}>
                                        <td style={{ padding: '8px 10px', fontSize: '0.82rem' }}>{item.note || '-'}</td>
                                        <td style={{ padding: '8px 10px', textAlign: 'right', fontSize: '0.82rem' }}>{item.energy}</td>
                                        <td style={{ padding: '8px 10px', textAlign: 'right', fontSize: '0.82rem' }}>{shortRM(item.cashPrice)}</td>
                                        <td style={{ padding: '8px 10px', textAlign: 'right', fontSize: '0.82rem' }}>{shortRM(item.cC36Price)}</td>
                                        <td style={{ padding: '8px 10px', textAlign: 'right', fontSize: '0.82rem' }}>{shortRM(item.cC60Price)}</td>
                                        <td style={{ padding: '4px', textAlign: 'right' }}>
                                            {canEdit && <>
                                                <IconButton size="small" onClick={() => openEditItem(item)}><EditIcon sx={{ fontSize: 14 }} /></IconButton>
                                                <IconButton size="small" onClick={() => handleDeleteItem(item.historyItemId)}><DeleteIcon sx={{ fontSize: 14, color: '#EF4444' }} /></IconButton>
                                            </>}
                                        </td>
                                    </tr>
                                ) : (
                                    <tr key={item.historyItemId} style={{ borderBottom: '1px solid #F1F5F9' }}>
                                        <td style={{ padding: '8px 10px', fontSize: '0.82rem' }}>{item.panelQty}</td>
                                        <td style={{ padding: '8px 10px', textAlign: 'right', fontSize: '0.82rem' }}>{item.energy}</td>
                                        <td style={{ padding: '8px 10px', textAlign: 'right', fontSize: '0.82rem' }}>{item.inverterSize || '-'}</td>
                                        <td style={{ padding: '8px 10px', textAlign: 'right', fontSize: '0.82rem' }}>{shortRM(item.cashPrice)}</td>
                                        <td style={{ padding: '8px 10px', textAlign: 'right', fontSize: '0.82rem' }}>{shortRM(item.cC36Price)}</td>
                                        <td style={{ padding: '8px 10px', textAlign: 'right', fontSize: '0.82rem' }}>{shortRM(item.cC60Price)}</td>
                                        <td style={{ padding: '8px 10px', textAlign: 'right', fontSize: '0.82rem' }}>{shortRM(item.cash3p8k)}</td>
                                        <td style={{ padding: '8px 10px', textAlign: 'right', fontSize: '0.82rem' }}>{shortRM(item.cC36_3p8k)}</td>
                                        <td style={{ padding: '8px 10px', textAlign: 'right', fontSize: '0.82rem' }}>{shortRM(item.cC60_3p8k)}</td>
                                        <td style={{ padding: '4px', textAlign: 'right' }}>
                                            {canEdit && <>
                                                <IconButton size="small" onClick={() => openEditItem(item)}><EditIcon sx={{ fontSize: 14 }} /></IconButton>
                                                <IconButton size="small" onClick={() => handleDeleteItem(item.historyItemId)}><DeleteIcon sx={{ fontSize: 14, color: '#EF4444' }} /></IconButton>
                                            </>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </Box>
                </Box>
            ))}

            <Dialog open={versionDialogOpen} onClose={() => setVersionDialogOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontFamily: PP, fontWeight: 700 }}>{versionDialogMode === 'create' ? 'New Snapshot' : 'Edit Snapshot'}</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: '8px !important' }}>
                    <TextField label="Name" value={versionForm.name} onChange={e => setVersionForm(p => ({ ...p, name: e.target.value }))} size="small" required />
                    <TextField label="Valid From" type="date" value={versionForm.validFrom} onChange={e => setVersionForm(p => ({ ...p, validFrom: e.target.value }))} size="small" InputLabelProps={{ shrink: true }} required />
                    <TextField label="Valid Until (optional)" type="date" value={versionForm.validUntil} onChange={e => setVersionForm(p => ({ ...p, validUntil: e.target.value }))} size="small" InputLabelProps={{ shrink: true }} />
                    <TextField label="Note (optional)" value={versionForm.note} onChange={e => setVersionForm(p => ({ ...p, note: e.target.value }))} size="small" multiline maxRows={3} />
                    {versionDialogMode === 'create' && (
                        <>
                            <Typography sx={{ fontSize: '0.78rem', fontFamily: PP, fontWeight: 600, mt: 1 }}>Seed rows from:</Typography>
                            <RadioGroup value={versionForm.seedChoice} onChange={e => setVersionForm(p => ({ ...p, seedChoice: e.target.value }))}>
                                <FormControlLabel value="live" control={<Radio size="small" />} label={<Typography sx={{ fontSize: '0.82rem', fontFamily: PP }}>Copy from Live prices</Typography>} />
                                <FormControlLabel value="existing" control={<Radio size="small" />} label={<Typography sx={{ fontSize: '0.82rem', fontFamily: PP }}>Copy from existing snapshot</Typography>} />
                                <FormControlLabel value="none" control={<Radio size="small" />} label={<Typography sx={{ fontSize: '0.82rem', fontFamily: PP }}>Start empty</Typography>} />
                            </RadioGroup>
                            {versionForm.seedChoice === 'existing' && (
                                <FormControl size="small">
                                    <InputLabel>Source snapshot</InputLabel>
                                    <Select value={versionForm.copyFromHistoryId} onChange={e => setVersionForm(p => ({ ...p, copyFromHistoryId: e.target.value }))} label="Source snapshot">
                                        {histories.map(h => (
                                            <MenuItem key={h.historyId} value={h.historyId}>{h.name}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}
                        </>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setVersionDialogOpen(false)} sx={{ fontFamily: PP, textTransform: 'none' }}>Cancel</Button>
                    <Button onClick={saveVersion} variant="contained" sx={{ background: 'linear-gradient(135deg, #1A8CFF, #0F62CC)', fontFamily: PP, fontWeight: 600, textTransform: 'none', borderRadius: '8px', boxShadow: 'none' }}>Save</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={itemDialogOpen} onClose={() => setItemDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontFamily: PP, fontWeight: 700 }}>{itemEdit?.historyItemId ? 'Edit Row' : 'Add Row'} — {itemEdit?.listName}</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: '8px !important' }}>
                    {isBatteryEdit ? (
                        <>
                            <TextField label="Battery Name / Type" value={itemEdit?.note || ''} onChange={f('note')} size="small" placeholder="e.g. 14.3kWh GoodWe" />
                            <TextField label="Energy (kWh)" type="number" value={itemEdit?.energy || 0} onChange={fn('energy')} size="small" />
                            <TextField label="Cash Price" type="number" value={itemEdit?.cashPrice || 0} onChange={fn('cashPrice')} size="small" />
                            <TextField label="CC 36M Price" type="number" value={itemEdit?.cC36Price || 0} onChange={fn('cC36Price')} size="small" />
                            <TextField label="CC 60M Price" type="number" value={itemEdit?.cC60Price || 0} onChange={fn('cC60Price')} size="small" />
                        </>
                    ) : (
                        <>
                            <TextField label="Panel Qty" type="number" value={itemEdit?.panelQty || 0} onChange={fn('panelQty')} size="small" />
                            <TextField label="Energy (kWp)" type="number" value={itemEdit?.energy || 0} onChange={fn('energy')} size="small" />
                            <TextField label="Inverter Size" value={itemEdit?.inverterSize || ''} onChange={f('inverterSize')} size="small" placeholder="e.g. 8kW" />
                            <TextField label="Cash Price" type="number" value={itemEdit?.cashPrice || 0} onChange={fn('cashPrice')} size="small" />
                            <TextField label="CC 36M Price" type="number" value={itemEdit?.cC36Price || 0} onChange={fn('cC36Price')} size="small" />
                            <TextField label="CC 60M Price" type="number" value={itemEdit?.cC60Price || 0} onChange={fn('cC60Price')} size="small" />
                            <TextField label="3P Inverter Size" value={itemEdit?.inverterSize3p || ''} onChange={f('inverterSize3p')} size="small" />
                            <TextField label="3P Cash Price" type="number" value={itemEdit?.cash3p8k || 0} onChange={fn('cash3p8k')} size="small" />
                            <TextField label="3P 36M Price" type="number" value={itemEdit?.cC36_3p8k || 0} onChange={fn('cC36_3p8k')} size="small" />
                            <TextField label="3P 60M Price" type="number" value={itemEdit?.cC60_3p8k || 0} onChange={fn('cC60_3p8k')} size="small" />
                        </>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => { setItemDialogOpen(false); setItemEdit(null); }} sx={{ fontFamily: PP, textTransform: 'none' }}>Cancel</Button>
                    <Button onClick={saveItem} variant="contained" sx={{ background: 'linear-gradient(135deg, #1A8CFF, #0F62CC)', fontFamily: PP, fontWeight: 600, textTransform: 'none', borderRadius: '8px', boxShadow: 'none' }}>Save</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
